import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionContext } from '@supabase/auth-helpers-react';
import {
  parseRecoveryCallback,
  resolveNativeRecoveryContinuation,
  WEB_RECOVERY_PATH,
} from '../lib/nativeRecoveryContinuation';
import NativeRecoveryAction from '../components/utility/NativeRecoveryAction';
import RequestResetLink from '../components/utility/RequestResetLink';
import { RecoveryPasswordForm, RecoverySuccess, RecoveryVerifying, RecoveryStoreLinks } from '../components/utility/RecoveryCompletionViews';
import { getIsAppPublished, getIsAndroidPublished, fetchAppPublishedStatus } from '../config/appStore';
import { detectPlatform, Platform } from '../lib/platform';
import { getRecoveryProof, subscribeRecoveryProof, RecoveryProof, cancelRecoveryImport, continueBrowserRecovery } from '../lib/recoverySession';
import { getCapturedRecoveryRoute, isCapturedRouteCurrent, clearCapturedRecoveryRoute } from '../lib/recoveryHandoff';
import { updatePasswordWithRecoveryToken } from '../lib/recoveryPassword';
import ComingSoonToast from '../components/ComingSoonToast';
import UtilityPageFrame from '../components/utility/UtilityPageFrame';

const MIN_PASSWORD_LENGTH = 8;

type Screen = 'choose' | 'nativeOnly' | 'verifying' | 'form' | 'success' | 'invalid' | 'request';

const PasswordResetRedirect: React.FC = () => {
  const { supabaseClient } = useSessionContext();
  const liveLocation = useLocation();
  // On the scrubbed bootstrap load use the captured snapshot (which still
  // holds the credentials); SPA navigation and tests use the live location.
  const captured = getCapturedRecoveryRoute();
  const route = isCapturedRouteCurrent(liveLocation) && captured ? captured : liveLocation;
  // Eager init so the first render resolves the correct platform target —
  // an Android first paint must never carry the iOS scheme.
  const [platform] = useState<Platform>(() => detectPlatform());
  const parsed = parseRecoveryCallback(route);
  const continuation = resolveNativeRecoveryContinuation(route, platform);
  const proof = useSyncExternalStore(subscribeRecoveryProof, getRecoveryProof);
  // 'failed' means provider/session validation ran and rejected. 'validated'
  // holds the exact proof that passed — render-time eligibility compares it
  // to the live tracker proof, so a swapped or cleared proof relocks the
  // form without waiting for state.
  const [failed, setFailed] = useState(false);
  const [validated, setValidated] = useState<RecoveryProof | null>(null);
  const [accountEmail, setAccountEmail] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  // Monotonic guard: any new proof/session/cleanup invalidates pending work.
  const seq = useRef(0);
  const submittingRef = useRef(false);
  // Immediate ref guard: two clicks in one frame must not verify twice.
  const importingRef = useRef(false);
  // Independent import-attempt generation — deliberately separate from the
  // validation `seq` so a published proof bumping seq cannot strand a
  // successful continuation as 'importing'. Route changes and unmount bump
  // it; a stale attempt may never write state or release a newer guard.
  const importAttemptRef = useRef(0);

  useEffect(() => {
    fetchAppPublishedStatus().catch(() => {});
    return () => {
      seq.current += 1;
      importAttemptRef.current += 1;
      cancelRecoveryImport();
      // Unmount means a real navigation (this component only exists on the
      // reset route); StrictMode's fake cleanup still sees the reset path in
      // window.location, so the snapshot survives it.
      if (typeof window !== 'undefined' && window.location.pathname !== WEB_RECOVERY_PATH) {
        clearCapturedRecoveryRoute();
      }
    };
  }, []);

  // A callback identity change (or a re-navigation to a different link)
  // while an import is in flight must deny the pending operation.
  const routeKey = `${route.search}|${route.hash}`;
  useEffect(() => {
    importAttemptRef.current += 1;
    cancelRecoveryImport();
    setFailed(false);
    setImporting(false);
    importingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  // Validate PASSWORD_RECOVERY proof outside the auth callback. A URL marker
  // (hash type=recovery or a query token) is never sufficient; the session
  // alone is not proof either. The SDK's own session is the authority — the
  // helper context ignores PASSWORD_RECOVERY, so on the ephemeral route
  // client `session` stays null while getSession() holds the exchange.
  // Provider getUser confirms the JWT's identity.
  useEffect(() => {
    setValidated(null);
    setAccountEmail('');
    if (!proof) return;
    const mySeq = ++seq.current;
    let alive = true;
    (async () => {
      try {
        const { data: sd, error: sessErr } = await supabaseClient.auth.getSession();
        const current = sd?.session;
        if (
          sessErr || !current ||
          current.user?.id !== proof.userId ||
          current.access_token !== proof.accessToken
        ) {
          if (alive && seq.current === mySeq) setFailed(true);
          return;
        }
        const { data, error } = await supabaseClient.auth.getUser(proof.accessToken);
        if (!alive || seq.current !== mySeq) return; // superseded — never unlock
        const user = data?.user;
        if (error || !user || user.id !== proof.userId) {
          setFailed(true);
          return;
        }
        setAccountEmail(user.email || '');
        setValidated(proof);
      } catch {
        if (alive && seq.current === mySeq) setFailed(true);
      }
    })();
    return () => { alive = false; seq.current += 1; }; // cancel pending validation on change
  }, [proof, supabaseClient]);

  const eligible =
    !!proof &&
    !!validated &&
    validated.userId === proof.userId &&
    validated.accessToken === proof.accessToken;

  const browserConsumable =
    parsed.status === 'ready' && (parsed.kind === 'tokenHash' || parsed.kind === 'implicit');

  const screen: Screen =
    done ? 'success'
    : eligible ? 'form'
    : failed ? 'invalid'
    : importing || proof ? 'verifying'
    : parsed.status === 'missing' ? 'request'
    : parsed.status !== 'ready' ? 'invalid'
    : parsed.kind === 'pkce' ? 'nativeOnly'
    : 'choose';

  // Explicit activation only — the browser path consumes the link. A ready
  // link that lands here was not claimed by an installed app, so the browser
  // choice is the primary one; Open RidePulse stays available alongside.
  const handleBrowserContinue = async () => {
    if (importingRef.current) return;
    importingRef.current = true;
    setImporting(true);
    const attempt = ++importAttemptRef.current;
    try {
      const result = await continueBrowserRecovery(supabaseClient, route);
      if (attempt !== importAttemptRef.current) return;
      if (result !== 'imported') setFailed(true);
      // 'imported' publishes screened proof; the validation effect above does
      // the provider getUser check before the form unlocks.
    } catch {
      if (attempt === importAttemptRef.current) setFailed(true);
    } finally {
      // Only this exact attempt may release the guard and the spinner —
      // a stale route's finally must not disturb a newer attempt.
      if (attempt === importAttemptRef.current) {
        importingRef.current = false;
        setImporting(false);
      }
    }
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter.';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter.';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number.';
    return null;
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return; // duplicate submit guard
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ text: 'Passwords do not match.', type: 'error' });
      return;
    }
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setMessage({ text: validationError, type: 'error' });
      return;
    }

    // Capture the current proof. Every mutation stays bound to this captured
    // recovery JWT — a proof/session change anywhere along the way cancels
    // the operation. The SDK session itself is the authority (the helper
    // context never sees the ephemeral PASSWORD_RECOVERY session).
    const capturedProof = getRecoveryProof();
    if (!capturedProof) {
      setFailed(true);
      return;
    }
    const mySeq = ++seq.current;
    submittingRef.current = true;
    setUpdating(true);
    const stillCurrent = () =>
      seq.current === mySeq &&
      getRecoveryProof() === capturedProof;

    try {
      const { data: sd, error: sessErr } = await supabaseClient.auth.getSession();
      const current = sd?.session;
      if (!stillCurrent()) return;
      if (
        sessErr || !current ||
        current.user?.id !== capturedProof.userId ||
        current.access_token !== capturedProof.accessToken
      ) {
        setFailed(true);
        return;
      }
      const { data, error: userError } = await supabaseClient.auth.getUser(capturedProof.accessToken);
      const user = data?.user;
      if (!stillCurrent()) return;
      if (userError || !user || user.id !== capturedProof.userId) {
        setFailed(true);
        return;
      }
      // Bound to the captured recovery JWT, not the ambient SDK session.
      await updatePasswordWithRecoveryToken(capturedProof.accessToken, newPassword);
      if (!stillCurrent()) return;

      setDone(true);
      setNewPassword('');
      setConfirmPassword('');
      // The recovery session is spent — revoke it (verifies proof identity
      // first so a rotated identity is never signed out by a stale write).
      if (getRecoveryProof() === capturedProof) {
        supabaseClient.auth.signOut().catch(() => {});
      }
    } catch {
      if (stillCurrent()) {
        setMessage({ text: 'Failed to update password. The link may have expired — request a new one below.', type: 'error' });
      }
    } finally {
      submittingRef.current = false;
      if (seq.current === mySeq) setUpdating(false);
    }
  };

  const cancelReset = () => {
    window.location.href = '/';
  };

  // Getters are read inside the handlers so the click uses the live flag.
  const handleAppStoreClick = (e: React.MouseEvent) => {
    if (!getIsAppPublished()) { e.preventDefault(); setShowComingSoon(true); }
  };
  const handlePlayStoreClick = (e: React.MouseEvent) => {
    if (!getIsAndroidPublished()) { e.preventDefault(); setShowComingSoon(true); }
  };

  const storeLinks = (
    <RecoveryStoreLinks
      platform={platform}
      onAppStoreClick={handleAppStoreClick}
      onPlayStoreClick={handlePlayStoreClick}
    />
  );

  if (screen === 'form') {
    return (
      <RecoveryPasswordForm
        accountEmail={accountEmail}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        onNewPasswordChange={v => { setNewPassword(v); setMessage(null); }}
        onConfirmPasswordChange={v => { setConfirmPassword(v); setMessage(null); }}
        updating={updating}
        message={message}
        onSubmit={handleResetPassword}
        onCancel={cancelReset}
        minPasswordLength={MIN_PASSWORD_LENGTH}
      />
    );
  }

  if (screen === 'verifying') {
    return <RecoveryVerifying />;
  }

  if (screen === 'success') {
    return <RecoverySuccess platform={platform} accountEmail={accountEmail} />;
  }

  const requestCopy = 'Enter your account email and we\'ll send a new reset link.';

  if (screen === 'nativeOnly') {
    // PKCE code links can only be exchanged by the client holding the
    // matching verifier — the app that issued the request has it, this
    // receiving browser does not. The app handoff is primary here; a fresh
    // browser-capable link is the fallback.
    return (
      <UtilityPageFrame title="Reset your password" context={
        <p className="utility-muted">
          This link finishes in the RidePulse app on the device that requested it.
          To reset in a browser instead, send yourself a new link below.
        </p>
      }>
        <div className="utility-card">
          {continuation.status === 'ready' && (
            <NativeRecoveryAction
              platform={platform}
              url={continuation.url}
              note={platform === 'other'
                ? 'Use this on an iPhone, iPad, or Android device with the RidePulse app installed.'
                : 'Open RidePulse to finish the reset in the app. The app checks the link.'}
            />
          )}
          <p className="utility-muted" style={{ marginBottom: '0.75rem' }}>
            No app, or the wrong device? {requestCopy}
          </p>
          <RequestResetLink client={supabaseClient} />
          {storeLinks}
        </div>
        {showComingSoon && <ComingSoonToast onClose={() => setShowComingSoon(false)} />}
      </UtilityPageFrame>
    );
  }

  if (screen === 'invalid' || screen === 'request') {
    return (
      <UtilityPageFrame title="Reset your password" context={
        <p className="utility-muted">
          {screen === 'invalid'
            ? 'This reset link is invalid, expired, or was already used.'
            : 'Forgot your password? ' + requestCopy}
        </p>
      }>
        <div className="utility-card">
          <div aria-live="polite">
            {screen === 'invalid' && (
              <div className="utility-message utility-message--error" role="alert">
                This reset link is invalid, expired, or was already used. Request a new one below.
              </div>
            )}
          </div>
          <RequestResetLink client={supabaseClient} />
          <p className="utility-muted utility-muted--small" style={{ marginTop: '1rem', textAlign: 'center' }}>
            You can also request a reset from "Forgot password" on the RidePulse sign-in screen.
          </p>
        </div>
      </UtilityPageFrame>
    );
  }

  // 'choose' — a valid, unconsumed link. Browser completion is primary;
  // native handoff stays explicit and available until consumed.
  return (
    <UtilityPageFrame title="Reset your password" context={
      <p className="utility-muted">
        Finish your reset in this browser — the app is optional.
      </p>
    }>
      <div className="utility-card">
        <button
          type="button"
          className="utility-button"
          disabled={importing}
          onClick={handleBrowserContinue}
          style={{ marginBottom: '0.75rem' }}
        >
          Continue in this browser
        </button>
        {browserConsumable && platform !== 'other' && continuation.status === 'ready' && (
          <NativeRecoveryAction
            platform={platform}
            url={continuation.url}
            quiet
            note={platform === 'android'
              ? 'Prefer the app? Open RidePulse with the updated Android recovery build installed.'
              : 'Prefer the app? Open RidePulse on this iPhone or iPad with the app installed.'}
            fallbackNote="RidePulse did not open — it may not be installed. Continue in this browser instead; your link is still usable here."
          />
        )}
        {storeLinks}
      </div>
      {showComingSoon && <ComingSoonToast onClose={() => setShowComingSoon(false)} />}
    </UtilityPageFrame>
  );
};

export default PasswordResetRedirect;
